/* eslint-disable no-useless-escape */
import { message, Modal } from "antd";
import moment from "moment";
import pathRegexp from "path-to-regexp";
import { parse } from "querystring";
import { EMAIL_REGEX } from "@/utils/constants";
import { LoadStatus, WeekDays, VehicleColor } from "@/utils/enum";
import { getAdminSingleVehicle, getDealerVehicle, getTransporterVehicle } from "@/services/vehicles";
import { parsePhoneNumber } from "@/components/PhoneNumberInput";

const { confirm } = Modal;

const reg =
  /(((^https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)$/;
export const isUrl = path => reg.test(path);

export const getPageQuery = () => parse(window.location.href.split("?")[1]);
/**
 * props.route.routes
 * @param router [{}]
 * @param pathname string
 */

export const getAuthorityFromRouter = (router = [], pathname) => {
  const authority = router.find(
    ({ routes, path = "/" }) =>
      (path && pathRegexp(path).exec(pathname)) || (routes && getAuthorityFromRouter(routes, pathname)),
  );

  if (authority) {
    return authority;
  }

  return undefined;
};

export const getRouteAuthority = (path, routeData) => {
  let authorities;

  routeData.forEach(route => {
    // match prefix
    if (pathRegexp(`${route.path}/(.*)`).test(`${path}/`)) {
      if (route.authority) {
        authorities = route.authority;
      } // exact match

      if (route.path === path) {
        authorities = route.authority || authorities;
      } // get children authority recursively

      if (route.routes) {
        authorities = getRouteAuthority(path, route.routes) || authorities;
      }
    }
  });

  return authorities;
};

export const formatPhoneNumber = (num, extension) => {
  if (!num) {
    return "";
  }

  const parsed = parsePhoneNumber(num);
  const { countryCode, areaCode, phoneNumber } = parsed;

  if (!countryCode || !areaCode || !phoneNumber) {
    return num;
  }

  return `+${countryCode} (${areaCode}) ${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, phoneNumber.length)}${
    extension ? `;${extension}` : ""
  }`;
};

/*
  Helper function to get the dealerRole of the current user for a given dealer's entityId.
  This needs to be used to check the role reliably under All Accounts, not currentUser.dealerRole
*/
export const getDealerRole = (allConnections, entityId) => {
  for (let i = 0; i < allConnections.length; i++) {
    if (allConnections[i].entityId === entityId) {
      return allConnections[i].dealerRole;
    }
  }

  return false;
};

/*
  Helper function to get the transporterRole of the current user for a given transporter's entityId.
  This needs to be used to check the role reliably under All Accounts, not currentUser.transporterRole
*/
export const getTransporterRole = (allConnections, entityId) => {
  for (let i = 0; i < allConnections.length; i++) {
    if (allConnections[i].entityId === entityId) {
      return allConnections[i].transporterRole;
    }
  }

  return false;
};

// get string to display based on load status and signature status
export const getLoadStatusTranslation = (status, signedAtPickup, signedAtDropoff) => {
  switch (status) {
    case LoadStatus.Active:
      return "Active";

    case LoadStatus.PickedUp:
      return "In Transit";

    // case LoadStatus.PickedUp:
    //   if (signedAtPickup) {
    //     return "Picked up With Signature";
    //   }
    //   return "Picked up Without Signature";

    case LoadStatus.Delivered:
      if (signedAtDropoff) {
        return "Delivered With Signature";
      }
      return "Delivered Without Signature";

    case LoadStatus.Completed:
      return "Completed";

    case LoadStatus.Incomplete:
      return "Canceled";
  }
};

// take vin number entered in vehicle add forms and validate using nhtsa api
export const validateVIN = vin => {
  return new Promise((resolve, reject) => {
    if (vin === null || vin === undefined || vin.length !== 17) reject("Invalid Vin. 17 characters required.");

    resolve(vin);
  });
};

export const handleAdminVinOverride = () => {
  return new Promise((resolve, reject) => {
    confirm({
      title: "Invalid Vin",
      content: "Autosled could not decode this VIN. Allow anyway?",
      onOk() {
        message.warning("Invalid Vin override.");
        resolve();
      },
      onCancel() {
        reject();
      },
    });
  });
};

export const hideJiraWidget = () => {
  const widget = document.querySelector("#jsd-widget");
  if (!widget) return;
  widget.style.display = "none";
};

export const showJiraWidget = () => {
  const widget = document.querySelector("#jsd-widget");
  if (!widget) return;
  widget.style.display = "inline";
};

/**
 * Used to take an array representing days of the week and return a string with an abbreviation for each day.
 * Ex: ["MON" "TUE", "SUN"] -> "MON, TUE, SUN"
 * Ex: ["SAT", "SUN"] -> "Weekends"
 * Ex: [] -> "None"
 *
 * @param {Array} restrictedDaysOfWeek Array of strings representing days of the week
 * @returns {string} Comma + Space delimited string of abbreviations of days of the week
 *                   OR "Weekends" in the case that the input is ["SAT", "SUN"]
 *                   OR "None" in the case that the input is null, undefined, or ""
 */
export const getRestrictedDaysOfWeekAsString = restrictedDaysOfWeek => {
  if (!restrictedDaysOfWeek || restrictedDaysOfWeek.length === 0) {
    return "None";
  }

  const isWeekends =
    restrictedDaysOfWeek.length === 2 &&
    restrictedDaysOfWeek.every(day => [WeekDays.SATURDAY, WeekDays.SUNDAY].includes(day));

  return isWeekends ? "WEEKENDS" : restrictedDaysOfWeek.join(", ");
};

export const disableDatesBeforeToday = date => {
  const today = moment();

  return date?.isBefore(today, "day");
};

export const getUrlParams = urlParams => {
  if (urlParams && urlParams?.length) {
    const params = new URLSearchParams(window.location.search);

    if (typeof urlParams === "string") {
      return params.get(urlParams);
    } else if (Array.isArray(urlParams)) {
      const result = {};

      urlParams.forEach(p => {
        result[p] = params.get(p);
      });

      return result;
    } else {
      return {};
    }
  } else {
    return {};
  }
};

export const setUrlParams = (urlParam, value) => {
  if (urlParam && typeof urlParam === "string") {
    const params = new URLSearchParams(window.location.search);

    if (value) {
      params.set(urlParam, value);
    } else {
      params.delete(urlParam);
    }

    window.history.replaceState({}, "", `${location.pathname}?${params}`);
  }
};

export const validateEmailAddress = email => EMAIL_REGEX.test(email);

export const validateGuid = guid => {
  if (guid && typeof guid === "string") {
    const actualGuid = guid.trim();

    if (actualGuid.length === 8) {
      return /^\w{8}$/.test(actualGuid);
    } else {
      return /^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$/.test(actualGuid);
    }
  } else {
    return false;
  }
};

export const debounce = (timerRef, cb, delay) => {
  // Reset the timeout setTimeout method execution
  clearTimeout(timerRef.current);

  // Execute the cb after delay time passed
  timerRef.current = setTimeout(cb, delay);
};

export function cleanObjectFromUndefined(obj) {
  const newObj = {};

  for (const key of Object.keys(obj)) {
    const value = obj[key];

    if (value !== undefined) {
      newObj[key] = value;
    }
  }

  return newObj;
}

export const dealerRoleLevels = {
  0: "N/A",
  1: "Admin",
  2: "Representative",
  3: "Clerk",
};

export const transporterRoleLevels = {
  0: "N/A",
  1: "Admin",
  2: "Strict",
  3: "Dispatcher",
  4: "Transporter",
};

export const quoteDeletetionReasonsMap = {
  0: "Not applicable",
  1: "Price",
  2: "No longer needed",
  3: "Deal fell through",
  4: "Vehical mechanical issues",
};

export const cancelShipmentReasons = [
  { value: 1, title: "Price" },
  { value: 2, title: "No Longer Needed" },
  { value: 3, title: "Deal Fell Through" },
  { value: 4, title: "Vehicle Mechanical Issue" },
  { value: 5, title: "Other" },
];

/**
 * Splits a Pascal-Case word into individual words separated by spaces.
 * @param {Object} word
 * @returns {String}
 * https://gist.github.com/JeffJacobson/3841577
 */
export const splitPascalCase = word => {
  var wordRe = /($[a-z])|[A-Z][^A-Z]+/g;
  return word.match(wordRe).join(" ");
};

export const removeWhitespaces = (str = "") => {
  return str.replace(/\s/g, "");
};

export const findRecordByRowKeyComposite = (record, rowKey, rowId) => {
  const rowIds = rowId?.split(rowKey.delimiter || "__-__");

  const rowKeysMatched = rowKey.keys.reduce((acc, cur, i) => {
    return (acc += +(record?.[cur] == rowIds?.[i]));
  }, 0);

  return rowKeysMatched === rowKey.keys.length;
};

export const findRecordByRowKey = (data, rowKey, rowId) => {
  return data.find(r => (rowKey?.composite ? findRecordByRowKeyComposite(r, rowKey, rowId) : r?.[rowKey] == rowId));
};

/**
 *  Used to check if the currentUser has the appropriate permissions to add users to the selected entity
 * @param {object} currentUser The currentUser object that is stored and accessed from redux throughout the project.
 *                             Check src -> models -> user.js to see where this is initialized.
 * @param {object} connectionRoleTypes The connectionRoleTypes object that is stored and accessed from redux throughout the project.
 *                                     Check src -> models -> user.js to see where this is initialized.
 * @param {object} entity Object with properties that match to the Entity.cs class in the Core API.
 * @returns {boolean} true if the currentUser can add users to the entity; false otherwise
 */
export const checkUserCanAddUsersToEntity = (currentUser, connectionRoleTypes, entity) => {
  if (!currentUser || !connectionRoleTypes || !entity) return false;
  const { type: entityType, entityId } = entity;
  const entityRepresentativeUserId = entity?.representative?.userId;
  const isDealerEntity = entityType === 1;

  if (isDealerEntity) {
    return checkUserCanAddUsersToDealerEntity(currentUser, connectionRoleTypes, entityId, entityRepresentativeUserId);
  } else {
    return checkUserCanAddUsersToTransporterEntity(currentUser, connectionRoleTypes, entityId);
  }
};

/**
 *  Used to check if the currentUser has the appropriate permissions to add users to the selected dealer entity
 * @param {object} currentUser The currentUser object that is stored and accessed from redux throughout the project.
 *                             Check src -> models -> user.js to see where this is initialized.
 * @param {object} connectionRoleTypes The connectionRoleTypes object that is stored and accessed from redux throughout the project.
 *                                     Check src -> models -> user.js to see where this is initialized.
 * @param {string} entityId GUID to a dealer entity.
 * @param {string} entityRepresentativeUserId GUID to the Autosled BDM that manages this dealer.
 * @returns {boolean} true if the currentUser can add users to the dealer entity; false otherwise
 */
const checkUserCanAddUsersToDealerEntity = (currentUser, connectionRoleTypes, entityId, entityRepresentativeUserId) => {
  const isAutosledEmployeeAndNotBDM = currentUser.autosledAdminLevel > 3 || currentUser.autosledAdminLevel === 1;
  const isAutosledBDM = currentUser.autosledAdminLevel === 3;
  const isAutosledRepForDealerEntity = entityRepresentativeUserId === currentUser.userId;

  const dealerRole = getDealerRole(connectionRoleTypes, entityId);
  const isDealerAdminRole = dealerRole === 1;

  return isDealerAdminRole || isAutosledEmployeeAndNotBDM || (isAutosledBDM && isAutosledRepForDealerEntity);
};

/**
 *  Used to check if the currentUser has the appropriate permissions to add users to the selected transporter entity
 * @param {object} currentUser The currentUser object that is stored and accessed from redux throughout the project.
 *                             Check src -> models -> user.js to see where this is initialized.
 * @param {object} connectionRoleTypes The connectionRoleTypes object that is stored and accessed from redux throughout the project.
 *                                     Check src -> models -> user.js to see where this is initialized.
 * @param {string} entityId GUID to a transporter entity
 * @returns {boolean} true if the currentUser can add users to the transporter entity; false otherwise
 */
const checkUserCanAddUsersToTransporterEntity = (currentUser, connectionRoleTypes, entityId) => {
  const isAutosledEmployeeAndNotBDM = currentUser.autosledAdminLevel > 3 || currentUser.autosledAdminLevel === 1;

  const transporterRole = getTransporterRole(connectionRoleTypes, entityId);
  const isTransporterAdminOrDispatcherRole = transporterRole === 1 || transporterRole === 3;

  return isTransporterAdminOrDispatcherRole || isAutosledEmployeeAndNotBDM;
};

/**
 * Take an object and trim any whitespace on properties that are strings. (Does not trim nested properties).
 * @param {object} payload
 * @returns A copy of the new object where all string type properties have been trimmed of whitespace.
 */
export const trimStringProperties = payload => {
  const trimmedPayload = {};
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return;

  Object.keys(payload).forEach(property => {
    if (typeof payload[property] === "string") {
      trimmedPayload[property] = payload[property].trim();
    } else {
      trimmedPayload[property] = payload[property];
    }
  });

  return trimmedPayload;
};

export const getGuidFirst8 = guid => guid?.split("-")?.[0]?.toUpperCase() || "";

export function getTrimPackageFromVinData(vinData) {
  const { name, make, model, trim } = vinData ?? {};

  return name ? name : [make, model, trim]?.filter(Boolean)?.join(" - ");
}

export const checkHasScrolledToBottomOfElement = element => {
  if (!element) return false;
  // console.log(element.scrollHeight);
  // console.log(element.scrollTop);
  // console.log(element.clientHeight);
  return Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) <= 1;
};

export function numberToPrice(number, currencySymbol = "$", fixed = null) {
  if (typeof fixed === "number") {
    number = number.toFixed(fixed);
  }

  const parts = String(number).split(".");
  const integerPart = parts[0];
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const formattedNumber = parts.length === 2 ? formattedInteger + "." + parts[1] : formattedInteger;

  return `${currencySymbol}${formattedNumber}`;
}

export function formatWithCommas(number) {
  if (!number && number !== 0) return "0";
  return number.toLocaleString("en-US");
}

export const copyToClipBoard = text => {
  navigator.clipboard.writeText(text || "").then(() => {
    message.success("Copied to clipboard!");
  });
};

export const DEFAULT_ID = "00000000-0000-0000-0000-000000000000";

/**
 * Take a string or number and return a formatted currency string.
 *
 * @param {number || string} value - The value to be formatted.
 * @param {string} locale - The locale to be used for formatting.
 * @param {string} currency - The currency to be used for formatting.
 * @param {number} maxDigits - The maximum number of significant digits to be displayed.
 * @returns {string} The formatted price.
 */
export const formatPrice = (value, locale = "en-US", currency = "USD", maxDigits = 2) => {
  if (!value) return "$0";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: maxDigits,
  }).format(value);
};

/**
 * Take a string or number and return a friendly formatted value with separators.
 *
 * @param {number | string} value - The value to be formatted.
 * @param {string} locale - The locale to be used for formatting.
 * @param {number} maxDigits - The maximum number of significant digits to be displayed.
 * @returns {string} The formatted distance.
 */
export const formatDistance = (value, locale = "en-US", maxDigits = 2) => {
  if (!value) return 0;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: value % 1 === 0 ? 0 : maxDigits, // No decimals for whole numbers
    maximumFractionDigits: maxDigits,
  }).format(value);
};

/**
 * Take a key-value pair and set it as a query parameter in the URL.
 * @param {string} key - The filter key to be added to the URL.
 * @param {string} value - The filter value to be added to the URL.
 */
export const addFilterToUrl = (key, value) => {
  if (!key || !value) return;

  const searchParams = new URLSearchParams(window.location.search);

  const currentFilters = searchParams.get("filters")?.split(",") || [];

  const updatedFilters = currentFilters.filter(filter => !filter.startsWith(`${key}:`));

  updatedFilters.push(`${key}:${value}`);

  searchParams.set("filters", updatedFilters.join(","));

  window.history.pushState({}, "", `?${searchParams.toString()}`);
};

/**
 * Take a key and remove it as a query parameter in the URL.
 * @param {string} key - The filter key to be removed from the URL.
 */
export const removeFilterFromUrl = key => {
  if (!key) return;

  const searchParams = new URLSearchParams(window.location.search);

  const currentFilters = searchParams.get("filters")?.split(",") || [];

  const updatedFilters = currentFilters.filter(filter => !filter.startsWith(`${key}:`));

  if (updatedFilters.length === 0) {
    searchParams.delete("filters");
  } else {
    searchParams.set("filters", updatedFilters.join(","));
  }

  window.history.pushState({}, "", `?${searchParams.toString()}`);
};

/**
 * Take key and return their value.
 * @param {string} key - The filter key to be added to the URL.
 * @returns {string} The filter value.
 */
export const getFilterFromUrl = key => {
  if (!key) return;

  const searchParams = new URLSearchParams(window.location.search);

  const currentFilters = searchParams.get("filters")?.split(",") || [];

  const filter = currentFilters.find(filter => filter.startsWith(`${key}:`));

  return filter?.split(":")?.at(1);
};

/**
 * Take a string and return a pascal case representation.
 * @param {string} value - The string to be transformed.
 * @returns {string} The string in pascal case.
 */
export const toPascalCase = value => {
  if (!value) return;

  return value
    .replace(/(^\w|[A-Z]|\b\w)/g, match => match.toUpperCase())
    .replace(/([A-Z])/g, match => match.toUpperCase());
};

/**
 * Take an object of date filters and return a date criteria params.
 * @param {object} filters - The date filters to be transformed.
 * @param {string} key - The date filters key name on URL.
 * @returns {string} The date criteria params with the following format `PropInPascal  YYYY-MM-DD/YYYY-MM-DD`.
 */
export const buildDateCriteria = (filters, key) => {
  if (!filters) return;

  const params = Object.entries(filters)
    .filter(([, value]) => value)
    .map(([propName, value]) => {
      const pascalCasePropName = toPascalCase(propName);
      return `${key}=${encodeURIComponent(pascalCasePropName + " " + value)}`;
    });

  return params.join("&");
};

/**
 * Transforms an array of property paths based on a provided mapping.
 *
 * @param {string[]} properties - An array of property paths to transform.
 * @param {Object} pathMapping - An object where keys are original property paths and values are target paths.
 * @returns {string[]} An array of transformed property paths.
 */
export const transformPropertyPaths = (properties = [], pathMapping = {}) => {
  if (!Array.isArray(properties) || properties.length === 0) {
    return [];
  }

  return properties.map(property => pathMapping[property] || property);
};

const configOptions = {
  removeUndefined: true,
  removeEmptyArrays: true,
  removeNull: true,
};

/**
 * Cleans an object from undefined values.
 *
 * @param {Object} obj - The object to be cleaned.
 * @param {configOptions} config - The configuration options.
 * @returns {Object} The cleaned object.
 */
export const cleanObj = (obj, config = configOptions) => {
  const isArray = Array.isArray(obj);
  const isEmptyArray = isArray && obj.length === 0;

  if (!obj || typeof obj !== "object" || (isArray && !isEmptyArray)) {
    return obj;
  }

  const newObj = {};

  for (const key of Object.keys(obj)) {
    const value = obj[key];

    if (config.removeUndefined && value === undefined) {
      continue;
    }

    if (config.removeEmptyArrays && isEmptyArray) {
      continue;
    }

    if (config.removeNull && value === null) {
      continue;
    }

    newObj[key] = cleanObj(value, config);
  }

  return newObj;
};

/**
 * Check if the addresses are unique
 *
 * @param {Object} pickupAddress - The pickup address object.
 * @param {Object} dropoffAddress - The dropoff address object.
 * @returns {Boolean} True if the addresses are unique, false otherwise.
 */
const propsToCheck = ["addressLine1", "city", "stateId", "zipCode"];

export const checkUniqueAddresses = (pickupAddress, dropoffAddress) => {
  if (!pickupAddress || !dropoffAddress) {
    return false;
  }

  // return false if all props are the same
  const arePropsEqual = propsToCheck.every(prop => pickupAddress[prop] === dropoffAddress[prop]);

  return !arePropsEqual;
};

/**
 * Safely parse a string as a number.
 *
 * @param {string} value - The string to parse.
 * @returns {number} The parsed number.
 */
export const parseAsNumber = (value, defaultValue = null) => {
  try {
    const parsed = Number(value);

    if (!isFinite(parsed)) {
      return defaultValue;
    }

    return parsed;
  } catch (e) {
    return defaultValue;
  }
};

/**
 * Safely parse the vehicle data from the vehicle object.
 *
 * @param {object} vehicle - The vehicle object to be parsed.
 * @returns {object} The parsed vehicle data.
 */
export const parseVehicleData = data => {
  if (!data) return {};

  const { color } = data || {};

  const usingCustomColor =
    color !== undefined &&
    color !== null &&
    (color === VehicleColor.Other || !Object.values(VehicleColor).some(value => value === color));

  return {
    ...data,
    trimPackageName: data.trimPackage,
    color: usingCustomColor ? VehicleColor.Other : color,
    customColor: usingCustomColor ? color : null,
    inoperational: data.drivabilityType,
    gatePassStatus: data.gatePassType,
    gatePassUrl: data.gatePassUrl,
  };
};

/**
 *
 *
 * @param {object} configPerm - The configuration permissions object.
 * @returns {function} The vehicle caller endpoint.
 */
export const getVehicleCaller = configPerm => {
  const isAdmin = !!configPerm["CoreService.AdminShipments"];
  const isTransporter = !!configPerm["CoreService.AssignedLoads"] || !!configPerm["CoreService.AvailableLoads"];

  if (isAdmin) {
    return getAdminSingleVehicle;
  }

  if (isTransporter) {
    return getTransporterVehicle;
  }

  return getDealerVehicle;
};
